import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

// Cancelamentos do período, sem o Tem em Casa (Adnéia, 26/10/2026, para a lista de números do
// Allan). Quatro linhas contadas por entrega: AES cancelado, atendimento encerrado como
// "Estornado", ETR por falta ou perda no estoque e Erro na Nota sem entrega nova. No AES,
// "cancelado de verdade" = a entrega está cancelada no BSeller e o pedido não ganhou entrega
// nova (nem similar, nem reenvio, nem o Tem em Casa); no ETR, a entrega parou (voltou ao galpão,
// devolvida, cancelada ou perdida) e não ganhou entrega nova (27/10); o Erro na Nota conta o
// que fechou sem entrega nova, sem olhar o BSeller (26/10). O % é cancelados ÷ entraram na mesma linha, no
// período. O total conta cada entrega uma vez. Sem dado, a célula mostra "?". Encerrado sem data
// de encerramento não tem dia e fica fora da conta: a linha mostra quantos foram (26/10). Os números vêm
// prontos da rota da aba Volume; a tela só mostra.
const CORES = { aes: '#3b82f6', atendimento: '#ef4444', etr: '#f59e0b', erro_nota: '#8b5cf6' };
const CURTO = { aes: 'AES', atendimento: 'Atendimento', etr: 'ETR', erro_nota: 'Erro na Nota' };

const num = (v) => (v === null || v === undefined ? '?' : v.toLocaleString('pt-BR'));
const pct = (v) => (v === null || v === undefined ? '?' : `${v.toLocaleString('pt-BR')}%`);
const diaMes = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}` : '?');
const rotuloPeriodo = (p, granularidade) => (granularidade === 'mes' ? `${p.slice(5, 7)}/${p.slice(2, 4)}` : diaMes(p));

const CancelamentosPeriodo = ({ dados }) => {
  if (!dados) return null;
  const { inicio, fim, banco_fora: bancoFora, granularidade, linhas = [], total_entregas: total,
    serie = [], motivos_atendimento: motivos = [] } = dados;
  const estornados = linhas.find(l => l.chave === 'atendimento')?.cancelados || 0;
  const semData = linhas.reduce((n, l) => n + (l.sem_data || 0), 0);
  const grafico = serie.map(s => ({ ...s, rotulo: rotuloPeriodo(s.periodo, granularidade) }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Cancelamentos do período (sem o Tem em Casa)</CardTitle>
        <CardDescription>
          {diaMes(inicio)} a {diaMes(fim)} · contado por entrega · AES: entrega cancelada no BSeller, sem
          entrega nova (nem similar, nem reenvio, nem Tem em Casa) · ETR: a entrega parou (voltou ao galpão,
          devolvida, cancelada ou perdida), sem entrega nova · Erro na Nota: fechou sem entrega nova · % =
          cancelados sobre os que entraram na mesma linha
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {bancoFora && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300" role="status">
            O banco da casa não respondeu: AES, ETR e o total ficam "?". O atendimento estornado e o Erro na
            Nota não dependem dele.
          </div>
        )}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Linha</TableHead>
                  <TableHead className="text-right">Cancelados</TableHead>
                  <TableHead className="text-right">Entraram</TableHead>
                  <TableHead className="text-right">% cancelados</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {linhas.map(l => (
                  <TableRow key={l.chave}>
                    <TableCell>
                      <span className="inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle" style={{ background: CORES[l.chave] }} />
                      {l.rotulo}
                      {l.sem_dado > 0 && (
                        <span className="ml-2 text-xs text-muted-foreground" title="Entregas que o banco da casa não conhece (no ETR, também a que não está cancelada e não tem rastreio): não entram na conta">
                          (+{l.sem_dado} sem dado)
                        </span>
                      )}
                      {l.sem_data > 0 && (
                        <span className="ml-2 text-xs text-amber-700 dark:text-amber-400" title="Encerrados sem data de encerramento: não dá para saber o dia, e ficam fora da conta">
                          (+{l.sem_data} encerrado{l.sem_data === 1 ? '' : 's'} sem data)
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-semibold">{num(l.cancelados)}</TableCell>
                    <TableCell className="text-right">{num(l.entraram)}</TableCell>
                    <TableCell className="text-right">{pct(l.pct)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell className="font-semibold">Total de entregas (cada uma conta uma vez)</TableCell>
                  <TableCell className="text-right font-bold">{num(total)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">—</TableCell>
                  <TableCell className="text-right text-muted-foreground">—</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            {semData > 0 && (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                {semData} caso{semData === 1 ? '' : 's'} que entr{semData === 1 ? 'ou' : 'aram'} no período
                {semData === 1 ? ' foi encerrado' : ' foram encerrados'} sem data de encerramento: não dá para saber
                o dia, e {semData === 1 ? 'ele fica' : 'eles ficam'} fora da conta.
              </p>
            )}
          </div>
          <div>
            <p className="text-sm font-medium mb-2">Motivos dos atendimentos estornados</p>
            {motivos.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhum atendimento estornado no período.</p>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {motivos.map(m => (
                  <div key={m.categoria} className="flex items-center justify-between gap-2 rounded bg-muted/40 px-2 py-1.5">
                    <span className="text-sm">{m.categoria}</span>
                    <span className="text-sm whitespace-nowrap">
                      <span className="font-semibold">{m.total}</span>
                      <span className="text-muted-foreground ml-2">{pct(m.pct)}</span>
                    </span>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground pt-1">Categoria do atendimento · de {estornados} estornado{estornados === 1 ? '' : 's'}</p>
              </div>
            )}
          </div>
        </div>
        <div>
          <p className="text-sm font-medium mb-2">Cancelados por {granularidade === 'mes' ? 'mês' : 'dia'}</p>
          {grafico.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum cancelamento no período.</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={grafico}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="rotulo" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  {Object.keys(CURTO).map(k => (
                    <Bar key={k} dataKey={k} name={CURTO[k]} stackId="c" fill={CORES[k]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CancelamentosPeriodo;
